import { Box } from "@ecco/ui/box";
import { Text } from "@ecco/ui/text";
import { focusOutline } from "@ecco/ui/css/focus";
import { FilterBase } from "@components/commerce/product-listing/filters/common/filter/FilterBase";
import scrollableListStyles from "@components/commerce/product-listing/filters/common/filterScrollableList.module.css";
import { useSearchableFilterOptions } from "@components/commerce/product-listing/filters/common/useSearchableFilterOptions";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import { Checkbox } from "@ecco/ui/checkbox";
import type { ChangeEvent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { localCheckboxStyle } from "@components/commerce/product-listing/filters/toolbar/item/common/CommonToolbarFilterStyles";
import { SelectDropdownItem } from "@ecco/ui/select-dropdown";
import dynamic from "next/dynamic";

const FilterSearchInput = dynamic(
    () =>
        import("@components/commerce/product-listing/filters/common/filter-search-input/FilterSearchInput").then(
            (module) => module.FilterSearchInput
        ),
    { ssr: false }
);

export interface CommonToolbarFilterProps {
    initialFilters: Partial<RefinementListItem>[];
    filterName: string;
    items: RefinementListItem[];
    refine: (value: string) => void;
    isToolbar: boolean;
    isLoading?: boolean;
    /** Search UI is only for the stores facet. */
    enableSearch?: boolean;
}

export const CommonToolbarFilter = ({
    initialFilters,
    items,
    refine,
    filterName,
    isToolbar,
    isLoading,
    enableSearch,
}: CommonToolbarFilterProps) => {
    // Local state to track optimistic checkbox states
    const [optimisticStates, setOptimisticStates] = useState<Record<string, boolean>>({});

    // Create lookup maps for performance optimization
    const itemsMap = useMemo(() => {
        const map = new Map<string, RefinementListItem>();
        items.forEach((item) => map.set(item.value, item));
        return map;
    }, [items]);

    const initialFiltersMap = useMemo(() => {
        const map = new Map<string, string>();
        initialFilters.forEach((filter) => {
            if (filter.label && filter.value) {
                map.set(filter.label, filter.value);
            }
        });
        return map;
    }, [initialFilters]);

    // Reset optimistic states when items change (after refine completes)
    // Use a more specific dependency to avoid unnecessary resets
    const itemsHash = useMemo(
        () => items.map((item) => `${item.value}:${item.isRefined}`).join("|"),
        [items]
    );

    useEffect(() => {
        setOptimisticStates({});
    }, [itemsHash]);

    // Handle checkbox selection with a single function
    const handleCheckboxSelection = useCallback(
        (event: ChangeEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>) => {
            let value: string | undefined;

            // prevent click/change event from bubbling up to parent elements
            event.stopPropagation();

            // Handle mouse events (button clicks)
            if (event.type === "click" && event.target) {
                const target = event.target as HTMLButtonElement;
                value = target.getAttribute("data-itemLabel") as string | undefined;
            }
            // Handle change events (checkbox changes)
            else if (event.type === "change" && event.target) {
                const target = event.target as HTMLInputElement;
                value = target.value;
            }

            if (!value) {
                // eslint-disable-next-line no-console
                console.warn("No value found in event target");
                return;
            }

            const itemValue = initialFiltersMap.get(value);
            if (!itemValue) return;

            const dynamicItem = itemsMap.get(itemValue);
            const currentState = optimisticStates[itemValue] ?? dynamicItem?.isRefined ?? false;

            setOptimisticStates((prev) => ({
                ...prev,
                [itemValue]: !currentState,
            }));

            refine(itemValue);
        },
        [itemsMap, initialFiltersMap, optimisticStates, refine]
    );

    const { searchQuery, setSearchQuery, isSearchable, filteredItems, resetSearch } =
        useSearchableFilterOptions(initialFilters ?? [], enableSearch);

    const searchableDropdownWidth = isSearchable ? "280px" : undefined;

    const dropdownItems = useMemo(
        () => (
            <Box
                className={!isSearchable ? scrollableListStyles.eccoScrollbar : undefined}
                minWidth={searchableDropdownWidth ?? ["auto", "210px"]}
                width={searchableDropdownWidth}
                padding={3}
                height={!isSearchable ? "auto" : undefined}
                maxHeight={!isSearchable ? "500px" : undefined}
                overflowY={!isSearchable ? "auto" : undefined}
            >
                {isSearchable ? (
                    <FilterSearchInput
                        value={searchQuery}
                        onChange={setSearchQuery}
                        ariaLabel={filterName}
                    />
                ) : null}
                <Box
                    className={
                        isSearchable
                            ? `${scrollableListStyles.searchableList} ${scrollableListStyles.eccoScrollbarStable} ${scrollableListStyles.searchableListToolbar}`
                            : undefined
                    }
                >
                    {filteredItems.map((item) => {
                        const dynamicItem = items.find((it) => it.value === item.value);

                        // Use optimistic state if available, otherwise use actual state
                        const isChecked =
                            optimisticStates[item.value] ?? dynamicItem?.isRefined ?? false;

                        return (
                            <SelectDropdownItem
                                as="div"
                                key={item.label}
                                sx={{
                                    ...localCheckboxStyle,
                                    _focusVisible: {
                                        ...focusOutline,
                                    },
                                    _focus: {
                                        background: "transparent",
                                    },
                                    _hover: { backgroundColor: "transparent", outline: "none" },
                                }}
                                data-itemLabel={item.label}
                                onClick={handleCheckboxSelection}
                                isDisabled={!dynamicItem}
                            >
                                <Checkbox
                                    _focusVisible={{
                                        ...focusOutline,
                                    }}
                                    _hover={{
                                        outline: "none",
                                    }}
                                    id={item.label}
                                    colorScheme={"black"}
                                    value={item.label}
                                    onChange={handleCheckboxSelection}
                                    isChecked={isChecked}
                                    isDisabled={!dynamicItem}
                                    _disabled={{
                                        cursor: "default !important",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "flex-start",
                                            gap: 1,
                                            whiteSpace: "normal",
                                        }}
                                    >
                                        <Text
                                            fontWeight={"bold"}
                                            sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                minW: 0,
                                            }}
                                        >
                                            {item.label}
                                        </Text>
                                        <Text color={"gray.700"} fontWeight={"bold"} flexShrink={0}>
                                            ({dynamicItem?.count ?? 0})
                                        </Text>
                                    </Box>
                                </Checkbox>
                            </SelectDropdownItem>
                        );
                    })}
                </Box>
            </Box>
        ),
        [
            filteredItems,
            filterName,
            handleCheckboxSelection,
            isSearchable,
            items,
            optimisticStates,
            searchableDropdownWidth,
            searchQuery,
            setSearchQuery,
        ]
    );

    if (initialFilters.length === 0) return null;

    return (
        <FilterBase
            isLoading={isLoading}
            dropdownItems={dropdownItems}
            filterName={filterName}
            isToolbar={isToolbar}
            onClose={resetSearch}
        />
    );
};
